import React, { useEffect, useState, useCallback } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Skeleton } from "./ui/skeleton";
import { Separator } from "./ui/separator";
import { EPCBadge } from "./EPCBadge";
import { PriorityBadge } from "./PriorityBadge";
import { api, API_BASE } from "../lib/api";
import { RefreshCw, ArrowRight, MapPin, Loader2, Calendar, Flame, Home, Wrench, ArrowUpRight, ArrowDownRight, Zap, FileText, Calculator, Database, PoundSterling, Plus, Trash2, FileUp, Target, Hammer, Download, CheckCircle2, ShieldAlert, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Textarea } from "./ui/textarea";

const STATE_COLORS = {
  planned:     { bg: "#f1f5f9", border: "#cbd5e1", text: "#334155" },
  instructed:  { bg: "#fef9c3", border: "#facc15", text: "#854d0e" },
  in_progress: { bg: "#fef3c7", border: "var(--hi-orange)", text: "var(--hi-orange-deep)" },
  completed:   { bg: "#dcfce7", border: "#22c55e", text: "#166534" },
  on_hold:     { bg: "#fee2e2", border: "#f87171", text: "#991b1b" },
  cancelled:   { bg: "#f1f5f9", border: "#94a3b8", text: "#475569" },
};
const STATE_LABELS = {
  planned: "Planned", instructed: "Instructed", in_progress: "In progress",
  completed: "Completed", on_hold: "On hold", cancelled: "Cancelled",
};

const fmtGBP = (n) => (n == null ? "—" : `£${Number(n).toLocaleString("en-GB", { maximumFractionDigits: 0 })}`);

export default function PropertySheet({ propertyId, onClose, onChanged }) {
  const [prop, setProp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [estimateLoading, setEstimateLoading] = useState(false);
  const [overrides, setOverrides] = useState({}); // {improvement_number: stringValue}

  const loadEstimate = useCallback(async () => {
    if (!propertyId) return;
    setEstimateLoading(true);
    try {
      const { data } = await api.get(`/properties/${propertyId}/cost-estimate`);
      setEstimate(data);
      const seed = {};
      (data.measures || []).forEach((m) => {
        if (m.manual_cost != null) seed[String(m.improvement_number)] = String(m.manual_cost);
      });
      setOverrides(seed);
    } finally {
      setEstimateLoading(false);
    }
  }, [propertyId]);

  useEffect(() => {
    if (!propertyId) { setProp(null); setEstimate(null); setOverrides({}); return; }
    setLoading(true);
    api.get(`/properties/${propertyId}`)
      .then(r => setProp(r.data))
      .finally(() => setLoading(false));
    loadEstimate();
  }, [propertyId, loadEstimate]);

  const check = async () => {
    setChecking(true);
    try {
      const { data } = await api.post(`/properties/${propertyId}/check`);
      toast.success(data.alerts_created > 0 ? `${data.alerts_created} alert(s) created` : "No changes detected");
      const r = await api.get(`/properties/${propertyId}`);
      setProp(r.data);
      onChanged?.();
    } catch {
      toast.error("Check failed");
    } finally {
      setChecking(false);
    }
  };

  const syncFromGov = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post(`/properties/${propertyId}/sync-gov-epc`);
      if (data.ok) {
        toast.success("Synced from gov.uk register", {
          description: `${data.alerts_generated} alert(s) · floor area ${data.current_epc?.total_floor_area ?? "—"} m²`,
        });
        const r = await api.get(`/properties/${propertyId}`);
        setProp(r.data);
        await loadEstimate();
        onChanged?.();
      } else {
        toast.warning("No certificate found on the gov register", {
          description: data.reason || "Address may not be in the EPC database",
        });
      }
    } catch (e) {
      toast.error("Gov sync failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSyncing(false);
    }
  };

  const setOverride = (num, val) => {
    setOverrides((prev) => ({ ...prev, [String(num)]: val }));
  };

  // ---- Manual measures (add/delete) -----------------------------------------
  const [newMeasure, setNewMeasure] = useState({ label: "", cost: "" });
  const [addingMeasure, setAddingMeasure] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [jobCardUploading, setJobCardUploading] = useState(false);
  const jobCardFileRef = React.useRef(null);

  const addCustomMeasure = async () => {
    const label = newMeasure.label.trim();
    const cost = Number(newMeasure.cost);
    if (label.length < 2) {
      toast.error("Label must be at least 2 characters");
      return;
    }
    if (!Number.isFinite(cost) || cost < 1000 || cost > 10000) {
      toast.error("Cost must be £1,000–£10,000");
      return;
    }
    setAddingMeasure(true);
    try {
      const { data } = await api.post(`/properties/${propertyId}/measures`, { label, cost });
      setEstimate(data);
      setNewMeasure({ label: "", cost: "" });
      toast.success("Measure added");
    } catch (e) {
      toast.error("Add failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setAddingMeasure(false);
    }
  };

  const deleteMeasure = async (measure) => {
    setDeletingId(measure.id);
    try {
      const { data } = await api.delete(`/properties/${propertyId}/measures/${measure.id}`);
      setEstimate(data);
      toast.success(measure.kind === "gov" ? "Measure hidden" : "Measure removed");
    } catch (e) {
      toast.error("Delete failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setDeletingId(null);
    }
  };

  const importJobCard = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const name = file.name.toLowerCase();
    if (!(name.endsWith(".xlsx") || name.endsWith(".xlsm") || name.endsWith(".csv") || name.endsWith(".pdf"))) {
      toast.error("Please upload an Excel (.xlsx / .xlsm), CSV or PDF file");
      return;
    }
    setJobCardUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/properties/${propertyId}/measures/from-job-card?replace=true`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setEstimate(data.cost_estimate);
      setVerification(data.verification);
      // Refresh delivery rows so the imported measures show up
      loadDelivery();
      const reached = data.verification?.reached_target;
      const skipped = data.skipped || 0;
      const fromSheet = data.verification?.source?.startsWith("job_card_sheet");
      const stdAdded = (data.contract_standard_added || []).length;
      let desc;
      if (fromSheet) {
        const preSap = data.verification.sheet_pre_install_sap;
        const preBd = data.verification.sheet_pre_install_band;
        const projSap = data.verification.projected_sap;
        const projBd = data.verification.projected_band;
        desc = reached
          ? `Sheet SAP ${preSap} (${preBd}) → projected SAP ${projSap} (${projBd}) ✓`
          : `Sheet SAP ${preSap} → ${projSap} — still ${data.verification.sap_gap} short of Band C`;
        if (stdAdded > 0) {
          desc += `  ·  topped-up with contract standard: ${data.contract_standard_added.join(", ")}`;
        }
      } else if (reached) {
        desc = `Job card reaches Band ${data.verification.projected_band} (SAP ${data.verification.projected_sap}) ✓`;
      } else if (data.verification?.ok) {
        desc = `Job card reaches SAP ${data.verification.projected_sap} — still ${data.verification.sap_gap} short of Band C`;
      } else {
        desc = "Re-run the gov sync if you need a SAP to verify against C";
      }
      toast.success(
        `Imported ${data.imported} measure(s)` +
          (data.format === "housing_jobcard" ? " from your structured job-card sheet" : ` from ${data.kind === "excel" ? "Excel" : "PDF"}`) +
          (stdAdded ? ` · +${stdAdded} contract-standard` : "") +
          (skipped ? ` · ${skipped} skipped` : ""),
        { description: desc }
      );
    } catch (e) {
      toast.error("Job card import failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setJobCardUploading(false);
    }
  };

  const downloadJobCardTemplate = async () => {
    try {
      const token = localStorage.getItem("epc_token");
      const res = await fetch(`${API_BASE}/jobcard-template.xlsx`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "HousingIntelligence-JobCard-Template.xlsx";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { a.remove(); URL.revokeObjectURL(url); }, 500);
      toast.success("Template downloaded — fill it in, then upload it back");
    } catch (e) {
      toast.error("Template download failed", { description: e.message });
    }
  };

  const saveOverrides = async () => {
    // Validate each: must be empty (=> remove) or 1000..10000
    const clean = {};
    for (const [k, v] of Object.entries(overrides)) {
      if (v === "" || v == null) continue;
      const n = Number(v);
      if (!Number.isFinite(n)) {
        toast.error(`Measure #${k}: not a number`);
        return;
      }
      if (n < 1000 || n > 10000) {
        toast.error(`Measure #${k}: must be £1,000 – £10,000`);
        return;
      }
      clean[k] = n;
    }
    try {
      const { data } = await api.patch(`/properties/${propertyId}/cost-estimate`, { overrides: clean });
      setEstimate(data);
      toast.success("Cost overrides saved");
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  // Downloading is fully server-side now: we POST to /certificate/{which},
  // the backend renders the official gov.uk certificate page to PDF via
  // headless Chrome (falling back to an internal ReportLab PDF for
  // spreadsheet-only properties), and we trigger a blob download. User
  // never leaves the app; the PDF auto-downloads with the gov cert content.
  const [downloading, setDownloading] = useState({ current: false, previous: false });
  const [pathPlan, setPathPlan] = useState(null);
  const [pathLoading, setPathLoading] = useState(false);
  const [renewablesView, setRenewablesView] = useState("priority"); // 'priority' | 'only'
  const [customPlanOpen, setCustomPlanOpen] = useState(false);
  const [customPlan, setCustomPlan] = useState(null); // server response: {saved, plan}
  const [heritage, setHeritage] = useState(null);
  const [verification, setVerification] = useState(null);
  const [delivery, setDelivery] = useState({}); // {measure_id: rec}
  const [editingDelivery, setEditingDelivery] = useState(null); // {measure_id, label, ...}

  const loadDelivery = useCallback(async () => {
    if (!propertyId) return;
    try {
      const { data } = await api.get(`/properties/${propertyId}/measures-delivery`);
      const map = {};
      (data.rows || []).forEach((r) => {
        map[r.id] = r;
      });
      setDelivery(map);
    } catch (e) {
      // silent — delivery is non-blocking
    }
  }, [propertyId]);

  useEffect(() => {
    if (!propertyId) return;
    setPathPlan(null);
    setPathLoading(true);
    // Use the compare endpoint so we always have both routes for the UI
    api.get(`/properties/${propertyId}/path-to-c/compare`)
      .then(r => setPathPlan(r.data))
      .catch(() => setPathPlan(null))
      .finally(() => setPathLoading(false));
    setCustomPlan(null);
    api.get(`/properties/${propertyId}/custom-plan`)
      .then(r => setCustomPlan(r.data))
      .catch(() => setCustomPlan(null));
    setHeritage(null);
    api.get(`/properties/${propertyId}/heritage`)
      .then(r => setHeritage(r.data))
      .catch(() => setHeritage(null));
    setVerification(null);
    api.get(`/properties/${propertyId}/job-card-verification`)
      .then(r => setVerification(r.data))
      .catch(() => setVerification(null));
    loadDelivery();
  }, [propertyId, loadDelivery]);
  const downloadCertificate = async (which) => {
    setDownloading((s) => ({ ...s, [which]: true }));
    try {
      const token = localStorage.getItem("epc_token");
      const res = await fetch(`${API_BASE}/properties/${propertyId}/certificate/${which}`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
        cache: "no-store",
      });
      const contentType = res.headers.get("content-type") || "";
      if (!res.ok) {
        // Surface the server's explanation so the user sees WHY nothing downloaded.
        let detail = `HTTP ${res.status}`;
        try {
          const err = contentType.includes("application/json")
            ? await res.json()
            : { detail: (await res.text()).slice(0, 240) };
          detail = err.detail || detail;
        } catch (_) { /* ignore */ }
        throw new Error(detail);
      }
      // Some legacy / spreadsheet-only paths could in theory return JSON despite
      // 200 — defensively check the Content-Type before treating as PDF.
      if (!contentType.includes("application/pdf")) {
        const raw = await res.text();
        let detail;
        try { detail = JSON.parse(raw)?.detail; } catch (_) { detail = null; }
        throw new Error(detail || "Server did not return a PDF for this certificate");
      }
      const source = res.headers.get("x-certificate-source") || "internal";
      const blob = await res.blob();
      // A truly empty / 0-byte download is the silent failure we keep hitting in
      // some browsers — guard against it explicitly.
      if (!blob || blob.size < 1024) {
        throw new Error("The certificate PDF came back empty. Click 'Sync from gov.uk' and try again.");
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers.get("content-disposition") || "";
      const m = cd.match(/filename="?([^";]+)"?/);
      a.download = m ? m[1] : `EPC-${which}-${propertyId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      // Some browsers (Safari, Firefox in some configs) need the anchor to live
      // for a tick before the download is triggered; small delay before cleanup.
      setTimeout(() => {
        a.remove();
        URL.revokeObjectURL(url);
      }, 500);
      toast.success(`${which.charAt(0).toUpperCase() + which.slice(1)} EPC downloaded · ${Math.round(blob.size / 1024)} KB`, {
        description: source === "gov"
          ? "Official gov.uk register certificate"
          : "Generated from cached property data (cert not on the live gov register)",
      });
    } catch (e) {
      toast.error("Download failed", { description: e.message || "Unknown error" });
    } finally {
      setDownloading((s) => ({ ...s, [which]: false }));
    }
  };

  const open = !!propertyId;

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto rounded-none border-l-2 border-slate-900" data-testid="property-sheet">
        <VisuallyHidden>
          <SheetTitle>Property details</SheetTitle>
        </VisuallyHidden>
        {loading || !prop ? (
          <div className="space-y-4 mt-6">
            <Skeleton className="h-10" />
            <Skeleton className="h-32" />
            <Skeleton className="h-20" />
          </div>
        ) : (
          <>
            <SheetHeader className="space-y-2">
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Property record</div>
              <SheetTitle className="font-heading font-black text-2xl tracking-tight pr-8">{prop.address}</SheetTitle>
              <SheetDescription className="flex flex-wrap gap-3 text-xs">
                <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /><span className="font-mono">{prop.postcode}</span></span>
                <span className="font-mono text-muted-foreground" data-testid="original-uprn">
                  Original UPRN: <span className="text-slate-900">{prop.original_uprn || prop.uprn || "—"}</span>
                </span>
                <span className="font-mono text-muted-foreground" data-testid="gov-uprn">
                  GOV UPRN: <span className={prop.gov_uprn ? "text-emerald-700" : "text-slate-400"}>{prop.gov_uprn || "not synced"}</span>
                </span>
                {prop.town && <span className="text-muted-foreground">{prop.town}</span>}
              </SheetDescription>

              {/* Wave 3 cohort banner — flags properties already delivered (Year 1)
                  or scheduled (Year 2) so nobody re-picks them by mistake. */}
              {prop.programme?.cohort === "wave3-y1" && (
                <div
                  className="border-l-4 border-emerald-700 bg-emerald-50 px-3 py-2 mt-2"
                  data-testid="programme-banner-y1"
                >
                  <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">
                    Wave 3 · Year 1 — already retrofitted
                  </div>
                  <div className="text-xs text-emerald-900 mt-0.5">
                    Status: <span className="font-mono">{(prop.programme.status || "complete").replace("_", " ")}</span>
                    {prop.programme.cph_status && (
                      <span className="ml-2 text-emerald-800/80">· CPH: {prop.programme.cph_status}</span>
                    )}
                  </div>
                </div>
              )}
              {prop.programme?.cohort === "wave3-y2" && (
                <div
                  className="border-l-4 border-sky-700 bg-sky-50 px-3 py-2 mt-2 flex items-center justify-between gap-3"
                  data-testid="programme-banner-y2"
                >
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-sky-700 font-bold">
                      Wave 3 · Year 2 — scheduled (in Asana)
                    </div>
                    <div className="text-xs text-sky-900 mt-0.5">
                      Do not re-select for the same scope — already in the delivery pipeline.
                      {prop.programme.asana_task_id && (
                        <span className="ml-2 font-mono text-sky-800/80">Asana #{prop.programme.asana_task_id}</span>
                      )}
                    </div>
                  </div>
                  {prop.programme.source === "manual_ui" && (
                    <button
                      type="button"
                      onClick={async () => {
                        if (!window.confirm("Return this property to the standby reserve list?")) return;
                        try {
                          const { data } = await api.post(`/properties/${prop.id}/programme`, { to: "wave3-y2-standby" });
                          setProp(data.property);
                          onChanged?.();
                          toast.success("Returned to standby");
                        } catch {
                          toast.error("Demote failed");
                        }
                      }}
                      className="text-[10px] uppercase tracking-wider font-bold border border-sky-700 bg-white text-sky-700 px-3 py-1.5 hover:bg-sky-700 hover:text-white transition-colors whitespace-nowrap"
                      data-testid="demote-to-standby-btn"
                    >
                      Return to standby
                    </button>
                  )}
                </div>
              )}
              {prop.programme?.cohort === "wave3-y2-standby" && (
                <div
                  className="border-l-4 border-violet-700 bg-violet-50 px-3 py-2 mt-2 flex items-center justify-between gap-3"
                  data-testid="programme-banner-standby"
                >
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-violet-700 font-bold">
                      Wave 3 · Year 2 — standby
                    </div>
                    <div className="text-xs text-violet-900 mt-0.5">
                      On the 200-property "ready if required" reserve list. Promote
                      to Year 2 if you have spare capacity in the programme.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const { data } = await api.post(`/properties/${prop.id}/programme`, { to: "wave3-y2" });
                        setProp(data.property);
                        onChanged?.();
                        toast.success("Promoted to Wave 3 · Year 2");
                      } catch {
                        toast.error("Promote failed");
                      }
                    }}
                    className="text-[10px] uppercase tracking-wider font-bold border border-violet-700 bg-white text-violet-700 px-3 py-1.5 hover:bg-violet-700 hover:text-white transition-colors whitespace-nowrap"
                    data-testid="promote-to-y2-btn"
                  >
                    Promote to Y2
                  </button>
                </div>
              )}
              {/* Heritage / planning badges */}
              {heritage?.constraints && heritage.constraints.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-1" data-testid="property-heritage-tags">
                  {heritage.constraints.map((c, i) => (
                    <span
                      key={i}
                      title={c.name}
                      className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border"
                      style={c.severity === "block"
                        ? { backgroundColor: "#fef2f2", color: "#7f1d1d", borderColor: "#dc2626" }
                        : { backgroundColor: "#fff8ed", color: "var(--hi-orange-deep)", borderColor: "var(--hi-orange)" }}
                      data-testid={`property-heritage-${c.dataset}`}
                    >
                      {c.label}
                    </span>
                  ))}
                </div>
              )}
            </SheetHeader>

            {/* Previous vs Current */}
            <div className="mt-6 grid grid-cols-2 gap-3" data-testid="epc-comparison">
              <EPCCard title="Previous EPC" epc={prop.previous_epc} onDownload={() => downloadCertificate("previous")} which="previous" downloading={downloading.previous} />
              <EPCCard title="Current EPC" epc={prop.current_epc} highlight onDownload={() => downloadCertificate("current")} which="current" downloading={downloading.current} />
            </div>

            {/* No-gov-cert warning */}
            {prop.no_gov_cert && (prop.current_epc?.band || prop.current_epc?.certificate_number) && (
              <div className="mt-3 border border-amber-300 bg-amber-50/60 px-3 py-2 text-xs text-amber-900 flex items-start gap-2" data-testid="no-gov-cert-warning">
                <Database className="h-3.5 w-3.5 mt-0.5" />
                <div>
                  <strong>Certificate not on the live gov.uk register</strong> — likely an older/expired cert.
                  Data shown is from the customer source-of-truth spreadsheet
                  (source: <span className="font-mono">{(prop.current_epc || {}).source || "customer_xlsx"}</span>).
                </div>
              </div>
            )}
            {prop.no_gov_cert && !prop.current_epc?.band && !prop.current_epc?.certificate_number && (
              <div className="mt-3 border border-slate-300 bg-slate-50/60 px-3 py-2 text-xs text-slate-700 flex items-start gap-2" data-testid="unknown-cert-warning">
                <Database className="h-3.5 w-3.5 mt-0.5" />
                <div>
                  <strong>No EPC certificate available</strong> for this address — neither the gov.uk register
                  nor the source-of-truth spreadsheet has a record. You may want to commission a fresh EPC.
                </div>
              </div>
            )}

            {/* EPC timeline (gov register history) */}
            {Array.isArray(prop.epc_history) && prop.epc_history.length > 1 && (
              <div className="mt-3 border border-slate-200" data-testid="epc-timeline">
                <div className="px-3 py-2 text-[10px] uppercase tracking-wider font-bold text-muted-foreground bg-slate-50 border-b border-slate-200">
                  EPC Timeline · gov register
                </div>
                {prop.epc_history.map((h, idx) => (
                  <div key={(h.certificate_number || "") + idx} className="px-3 py-2 border-b border-slate-100 last:border-b-0 flex items-center gap-3 text-xs" data-testid={`timeline-row-${idx}`}>
                    <span className="font-mono text-slate-500 w-20">{h.lodgement_date ? new Date(h.lodgement_date).toLocaleDateString("en-GB") : "—"}</span>
                    <EPCBadge band={h.band} size="sm" />
                    {h.expired && (
                      <span className="px-1 py-0.5 text-[9px] font-bold uppercase tracking-wider border border-red-600 text-red-700 bg-red-50">Expired</span>
                    )}
                    <span className="flex-1 font-mono text-slate-400 truncate">{h.certificate_number || ""}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Retrofit Priority */}
            {prop.retrofit && (
              <div className="mt-3 border border-border p-3 flex items-start gap-3" data-testid="retrofit-block">
                <Zap className="h-4 w-4 mt-0.5 text-amber-600" />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">Retrofit Priority</span>
                    <PriorityBadge priority={prop.retrofit.priority} score={prop.retrofit.score} size="sm" />
                  </div>
                  {prop.retrofit.reasons?.length > 0 && (
                    <div className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
                      {prop.retrofit.reasons.join(" · ")}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="mt-3 flex items-center justify-between text-xs gap-2 flex-wrap">
              <div className="text-muted-foreground">
                Last checked: <span className="font-mono">{prop.last_checked ? new Date(prop.last_checked).toLocaleString("en-GB") : "never"}</span>
                {prop.last_gov_sync && <span className="ml-2">· Gov synced <span className="font-mono">{new Date(prop.last_gov_sync).toLocaleDateString("en-GB")}</span></span>}
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={syncFromGov} disabled={syncing} className="rounded-none border-emerald-700 text-emerald-800 hover:bg-emerald-50" data-testid="sheet-sync-gov">
                  {syncing ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Database className="h-3.5 w-3.5 mr-1.5" />}
                  Sync from gov.uk
                </Button>
                <Button size="sm" onClick={check} disabled={checking} className="rounded-none bg-slate-900 hover:bg-slate-800" data-testid="sheet-check-now">
                  {checking ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
                  Check now
                </Button>
              </div>
            </div>

            <Separator className="my-6" />

            {/* Survey status — contact-attempt tracking for Unknown-band / non-EPC stock */}
            <SurveyStatusPanel
              prop={prop}
              onSaved={async () => {
                const r = await api.get(`/properties/${propertyId}`);
                setProp(r.data);
                onChanged?.();
              }}
            />

            {/* Heritage / planning constraint panel — what the property sits in
                AND what that means for each retrofit measure. */}
            <HeritagePanel
              heritage={heritage}
              propertyId={propertyId}
              onScanned={async () => {
                const r = await api.get(`/properties/${propertyId}`);
                setProp(r.data);
                const h = await api.get(`/properties/${propertyId}/heritage`);
                setHeritage(h.data);
                onChanged?.();
              }}
            />

            <Separator className="my-6" />

            {/* Path-to-C Planner — Cheapest / Quickest / Renewables / No-planning + custom */}
            <div data-testid="path-to-c-block">
              <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-2">
                  <Target className="h-3.5 w-3.5" /> Path to Band C · RdSAP 10 planner
                </div>
                {pathPlan?.ok && !pathPlan.already_at_c && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-[11px] uppercase tracking-wider font-bold"
                    onClick={() => setCustomPlanOpen(true)}
                    data-testid="customise-plan-btn"
                  >
                    <Wrench className="h-3 w-3 mr-1.5" />
                    {customPlan?.saved ? "Edit custom plan" : "Customise"}
                  </Button>
                )}
              </div>
              {pathLoading ? (
                <Skeleton className="h-24" />
              ) : !pathPlan?.ok ? (
                <div className="border border-dashed border-slate-300 p-4 text-xs text-slate-600">
                  Path-to-C cannot be computed for this property — no current SAP rating or band on file.
                  Run <strong>Sync from gov.uk</strong> above to populate it.
                </div>
              ) : pathPlan.already_at_c ? (
                <div className="p-4 text-xs leading-relaxed flex items-start gap-3" style={{ backgroundColor: "#f0fdf4", border: "1px solid #16a34a" }} data-testid="path-already-at-c">
                  <Target className="h-4 w-4 mt-0.5 text-emerald-700" />
                  <div>
                    <div className="font-bold text-emerald-900">Already at Band {pathPlan.current_band} (SAP {pathPlan.current_sap}).</div>
                    <div className="text-emerald-800/80 mt-1">No retrofit programme required to meet the band-C target — this property is already there.</div>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Hero comparison */}
                  <div className="grid grid-cols-12 gap-3 items-center p-3" style={{ backgroundColor: "var(--hi-cream)", border: "1px solid var(--hi-border)" }}>
                    <div className="col-span-3 text-center">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Today</div>
                      <div className="mt-1"><EPCBadge band={pathPlan.current_band} size="lg" /></div>
                      <div className="text-[10px] font-mono mt-1">SAP {pathPlan.current_sap}{pathPlan.current_sap_derived && <span className="text-amber-700">*</span>}</div>
                    </div>
                    <div className="col-span-1 text-center text-2xl text-slate-300">→</div>
                    <div className="col-span-3 text-center">
                      <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color: "var(--hi-orange-deep)" }}>Target</div>
                      <div className="mt-1"><EPCBadge band="C" size="lg" /></div>
                      <div className="text-[10px] font-mono mt-1">SAP ≥ 69</div>
                    </div>
                    <div className="col-span-5 text-[11px] leading-snug">
                      <div className="font-bold mb-1" style={{ color: "var(--hi-olive)" }}>Trade-off summary</div>
                      <div className="text-muted-foreground">
                        Cheapest saves <span className="font-bold text-emerald-700">£{(pathPlan.delta?.cost_saved_going_cheapest || 0).toLocaleString("en-GB")}</span> but may add measure(s).
                        Quickest saves <span className="font-bold" style={{ color: "var(--hi-orange-deep)" }}>{pathPlan.delta?.measures_saved_going_quickest || 0} measure(s)</span> on site.
                      </div>
                    </div>
                  </div>

                  {/* Saved custom plan banner */}
                  {customPlan?.saved && customPlan?.plan?.ok && (
                    <SavedCustomPlanCard
                      saved={customPlan.saved}
                      plan={customPlan.plan}
                      onEdit={() => setCustomPlanOpen(true)}
                      onClear={async () => {
                        if (!window.confirm("Clear the saved custom plan and revert to the auto routes?")) return;
                        try {
                          await api.delete(`/properties/${propertyId}/custom-plan`);
                          setCustomPlan(null);
                          toast.success("Custom plan cleared.");
                        } catch (e) {
                          toast.error("Could not clear custom plan.");
                        }
                      }}
                    />
                  )}

                  {/* Route cards — wrap so 4 fit on wide screens, 2 on medium */}
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
                    <RouteCard
                      title="Cheapest"
                      subtitle="Lowest £ to Band C"
                      accent="#15803d"
                      bgAccent="#f0fdf4"
                      route={pathPlan.cheapest}
                      testId="route-cheapest"
                    />
                    <RouteCard
                      title="Quickest"
                      subtitle="Fewest measures on site"
                      accent="var(--hi-orange-deep)"
                      bgAccent="#fff8ed"
                      route={pathPlan.quickest}
                      testId="route-quickest"
                    />
                    {(pathPlan.renewables_priority || pathPlan.renewables_only) && (
                      <RenewablesRouteCard
                        priority={pathPlan.renewables_priority}
                        only={pathPlan.renewables_only}
                        view={renewablesView}
                        onViewChange={setRenewablesView}
                        extraCost={pathPlan.delta?.extra_cost_renewables}
                      />
                    )}
                    {pathPlan.no_planning && (
                      <RouteCard
                        title="No planning"
                        subtitle="Skip consent-heavy measures"
                        accent="#1d4ed8"
                        bgAccent="#eff6ff"
                        route={pathPlan.no_planning}
                        testId="route-no-planning"
                        badge={pathPlan.no_planning.reached_target
                          ? `+£${Math.max(0, pathPlan.delta?.extra_cost_no_planning || 0).toLocaleString("en-GB")} vs cheapest`
                          : "Cannot reach C without planning"}
                      />
                    )}
                  </div>

                  <div className="text-[10px] text-muted-foreground px-1 leading-relaxed">
                    {pathPlan.current_sap_derived && (
                      <div><span className="text-amber-700">*</span> SAP derived from band mid-point (gov register has no numeric SAP cached). </div>
                    )}
                    RdSAP 10 / DESNZ guidance — confirm on site by an accredited assessor.
                  </div>
                </div>
              )}

              {/* Custom-plan editor dialog */}
              {customPlanOpen && (
                <CustomPlanEditor
                  propertyId={propertyId}
                  open={customPlanOpen}
                  onClose={() => setCustomPlanOpen(false)}
                  currentSap={pathPlan?.current_sap}
                  currentBand={pathPlan?.current_band}
                  initialKeys={
                    customPlan?.saved?.measure_keys
                    || (pathPlan?.cheapest?.measures || []).map(m => m.matched_library_key || m.key).filter(k => k && !k.startsWith("gov-"))
                  }
                  onSaved={(saved) => {
                    setCustomPlan(saved);
                    setCustomPlanOpen(false);
                    toast.success("Custom plan saved.");
                  }}
                />
              )}
            </div>

            <Separator className="my-6" />

            {/* Retrofit Cost Estimator */}
            <div data-testid="cost-estimator-block">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-2">
                  <Calculator className="h-3.5 w-3.5" /> Retrofit Cost Estimator
                </div>
                {estimate?.floor_area_m2 && (
                  <div className="text-xs font-mono text-muted-foreground">
                    Floor area <span className="font-bold text-slate-900">{estimate.floor_area_m2} m²</span>
                    {estimate.potential_band && (
                      <span className="ml-2">→ potential band <span className="font-bold text-emerald-700">{estimate.potential_band}</span></span>
                    )}
                  </div>
                )}
              </div>

              {estimateLoading ? (
                <Skeleton className="h-32" />
              ) : !estimate?.has_certificate_detail ? (
                <div className="border border-dashed border-slate-300 p-4 text-xs text-slate-600 leading-relaxed">
                  No gov-recommended retrofit measures cached yet. Click <strong>Sync from gov.uk</strong> above to pull
                  the full EPC certificate (floor area &amp; suggested improvements) from the live register, or use the
                  controls below to start a custom retrofit programme for this property.
                </div>
              ) : (estimate.measures || []).length === 0 ? (
                <div className="border border-slate-200 p-4 text-xs text-slate-600">
                  The current certificate lists no recommended improvements — add a measure below or upload a job card.
                </div>
              ) : (
                <div className="border border-slate-200">
                  <div className="grid grid-cols-12 gap-2 px-3 py-2 text-[10px] uppercase tracking-wider font-bold text-muted-foreground bg-slate-50 border-b border-slate-200">
                    <div className="col-span-5">Measure</div>
                    <div className="col-span-2 text-right">Auto</div>
                    <div className="col-span-4 text-right">Manual £ (1k–10k)</div>
                    <div className="col-span-1" />
                  </div>
                  {(estimate.measures || []).map((m) => {
                    const isCustom = m.kind === "custom";
                    return (
                      <div key={m.id} className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-slate-100 items-center text-sm" data-testid={`measure-row-${m.id}`}>
                        <div className="col-span-5">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium leading-snug">{m.label}</span>
                            {isCustom && (
                              <span
                                className="px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-bold rounded-sm"
                                style={
                                  m.source === "contract_standard"
                                    ? { backgroundColor: "#dbeafe", color: "#1e40af", border: "1px solid #93c5fd" }
                                    : { backgroundColor: "var(--hi-orange)", color: "white" }
                                }
                                data-testid={`measure-kind-${m.id}`}
                              >
                                {m.source === "contract_standard" ? "Contract std" : m.source === "job_card" ? "Job card" : "Custom"}
                              </span>
                            )}
                            {(() => {
                              const dRec = delivery[m.id];
                              const st = dRec?.state;
                              if (!st || st === "planned") return null;
                              const sc = STATE_COLORS[st] || STATE_COLORS.planned;
                              return (
                                <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-bold border"
                                      style={{ backgroundColor: sc.bg, borderColor: sc.border, color: sc.text }}
                                      data-testid={`measure-delivery-state-${m.id}`}>
                                  {STATE_LABELS[st]}
                                </span>
                              );
                            })()}
                            {m.heritage_risk && (
                              <span
                                className="px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-bold border inline-flex items-center gap-0.5"
                                style={{ backgroundColor: "#fff7ed", color: "#9a3412", borderColor: "#fed7aa" }}
                                title={`${m.heritage_risk.reason} · ${m.heritage_risk.lead_time_months} month lead time${m.heritage_risk.listed_building_consent ? " · Listed Building Consent required" : ""}`}
                                data-testid={`measure-heritage-risk-${m.id}`}
                              >
                                <ShieldAlert className="h-2.5 w-2.5" /> Planning
                              </span>
                            )}
                          </div>
                          <div className="text-[10px] font-mono text-muted-foreground">
                            {isCustom
                              ? (m.notes ? m.notes : "Manually added · saved")
                              : (m.gov_indicative_cost
                                  ? `gov ${m.gov_indicative_cost}`
                                  : `calc £${m.calc_range?.low?.toLocaleString("en-GB")}–£${m.calc_range?.high?.toLocaleString("en-GB")}`)}
                          </div>
                        </div>
                        <div className="col-span-2 text-right font-mono">{fmtGBP(m.auto_cost)}</div>
                        <div className="col-span-4">
                          {isCustom ? (
                            <div className="text-right font-mono text-sm font-bold pr-2" data-testid={`measure-custom-cost-${m.id}`}>
                              {fmtGBP(m.applied_cost)}
                            </div>
                          ) : (
                            <div className="relative">
                              <PoundSterling className="h-3 w-3 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                              <Input
                                type="number"
                                min={1000}
                                max={10000}
                                step={50}
                                placeholder="auto"
                                value={overrides[String(m.improvement_number)] ?? ""}
                                onChange={(e) => setOverride(m.improvement_number, e.target.value)}
                                className="rounded-none border-slate-300 h-8 pl-6 text-right font-mono text-xs"
                                data-testid={`measure-override-${m.improvement_number}`}
                              />
                            </div>
                          )}
                        </div>
                        <div className="col-span-1 flex justify-end gap-0.5">
                          {(() => {
                            const dRec = delivery[m.id];
                            const state = dRec?.state || "planned";
                            const sc = STATE_COLORS[state] || STATE_COLORS.planned;
                            return (
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => setEditingDelivery({ measure_id: m.id, label: m.label, ...dRec })}
                                className="h-7 w-7 px-0"
                                title={`Delivery state: ${STATE_LABELS[state]}${dRec?.contractor ? " · " + dRec.contractor : ""}`}
                                data-testid={`delivery-state-${m.id}`}
                                style={{ color: sc.text }}
                              >
                                <Hammer className="h-3.5 w-3.5" />
                                <span className="sr-only">{state}</span>
                              </Button>
                            );
                          })()}
                          <Button
                            size="icon"
                            variant="ghost"
                            disabled={deletingId === m.id}
                            onClick={() => deleteMeasure(m)}
                            className="h-7 w-7 text-slate-400 hover:text-red-700 hover:bg-red-50"
                            title={isCustom ? "Remove this measure" : "Hide this gov-recommended measure from the programme"}
                            data-testid={`delete-measure-${m.id}`}
                          >
                            {deletingId === m.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                          </Button>
                        </div>
                      </div>
                    );
                  })}

                  {/* Add custom measure inline row */}
                  <div className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-slate-100 items-center text-sm bg-amber-50/30" data-testid="add-measure-row">
                    <div className="col-span-5">
                      <Input
                        placeholder="Add a measure (e.g. Loft insulation 300mm)"
                        value={newMeasure.label}
                        onChange={(e) => setNewMeasure((s) => ({ ...s, label: e.target.value }))}
                        className="rounded-none border-slate-300 h-8 text-xs"
                        data-testid="new-measure-label"
                      />
                    </div>
                    <div className="col-span-2" />
                    <div className="col-span-4">
                      <div className="relative">
                        <PoundSterling className="h-3 w-3 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input
                          type="number"
                          min={1000}
                          max={10000}
                          step={50}
                          placeholder="cost"
                          value={newMeasure.cost}
                          onChange={(e) => setNewMeasure((s) => ({ ...s, cost: e.target.value }))}
                          className="rounded-none border-slate-300 h-8 pl-6 text-right font-mono text-xs"
                          data-testid="new-measure-cost"
                        />
                      </div>
                    </div>
                    <div className="col-span-1 flex justify-end">
                      <Button
                        size="icon"
                        variant="outline"
                        disabled={addingMeasure}
                        onClick={addCustomMeasure}
                        className="h-7 w-7 rounded-none border-slate-300"
                        data-testid="add-measure-btn"
                        title="Add this measure to the programme"
                      >
                        {addingMeasure ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-12 gap-2 px-3 py-3 bg-slate-50 border-t border-slate-200 items-center">
                    <div className="col-span-5 text-xs font-semibold uppercase tracking-wider">Programme Total</div>
                    <div className="col-span-2 text-right font-mono text-sm">{fmtGBP(estimate.totals?.auto_cost)}</div>
                    <div className="col-span-4 text-right font-mono text-sm font-bold" data-testid="cost-total-applied">{fmtGBP(estimate.totals?.applied_cost)}</div>
                    <div className="col-span-1" />
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between mt-2 gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="file"
                    accept=".xlsx,.xlsm,.csv,.pdf,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel.sheet.macroEnabled.12,text/csv"
                    ref={jobCardFileRef}
                    onChange={importJobCard}
                    className="hidden"
                    data-testid="job-card-file-input"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={jobCardUploading}
                    onClick={() => jobCardFileRef.current?.click()}
                    className="rounded-none border-slate-300"
                    data-testid="upload-job-card-btn"
                    title="Upload an Excel job card (preferred) or PDF. Excel uses the downloadable template and is far more reliable than PDF parsing."
                  >
                    {jobCardUploading
                      ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                      : <FileUp className="h-3.5 w-3.5 mr-1.5" />}
                    {jobCardUploading ? "Parsing job card…" : "Upload job card (Excel · CSV · PDF)"}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={downloadJobCardTemplate}
                    className="rounded-none text-xs"
                    data-testid="download-template-btn"
                    title="Download the Excel template — fill it in, then upload it above"
                  >
                    <Download className="h-3.5 w-3.5 mr-1.5" /> Template
                  </Button>
                </div>
                {estimate?.has_certificate_detail && (estimate.measures || []).some((m) => m.kind !== "custom") && (
                  <Button size="sm" variant="outline" onClick={saveOverrides} className="rounded-none border-slate-300" data-testid="save-cost-overrides">
                    Save overrides
                  </Button>
                )}
              </div>

              {/* Job-card path-to-C verification banner */}
              {verification?.ok && (estimate?.measures || []).some((m) => m.source === "job_card") && (
                <div
                  className="mt-3 p-3 border-2 flex items-start gap-3"
                  style={verification.reached_target
                    ? { backgroundColor: "#f0fdf4", borderColor: "#16a34a" }
                    : { backgroundColor: "#fff8ed", borderColor: "var(--hi-orange)" }}
                  data-testid="job-card-verification"
                >
                  {verification.reached_target ? <CheckCircle2 className="h-5 w-5 mt-0.5 text-emerald-700" /> : <Target className="h-5 w-5 mt-0.5" style={{ color: "var(--hi-orange-deep)" }} />}
                  <div className="text-xs leading-relaxed flex-1">
                    {verification.source?.startsWith("job_card_sheet") ? (
                      <>
                        <div className={`font-bold ${verification.reached_target ? "text-emerald-900" : "text-amber-900"}`}>
                          {verification.reached_target
                            ? `Job card lifts SAP ${verification.sheet_pre_install_sap} Band ${verification.sheet_pre_install_band} → SAP ${verification.projected_sap} Band ${verification.projected_band} ✓`
                            : `Job card lifts SAP ${verification.sheet_pre_install_sap} → ${verification.projected_sap} — still ${verification.sap_gap} short of Band C`}
                        </div>
                        <div className="text-emerald-800/80 mt-1">
                          Pre/Post-install SAP read directly from the customer's job-card sheet.
                          {verification.contract_uplift_added > 0 && (
                            <span className="block mt-1 text-blue-900/80">
                              Bumped +{verification.contract_uplift_added} SAP from contract-standard measures the sheet didn't list (sheet alone said {verification.sheet_post_install_band}{verification.sheet_post_install_sap}).
                            </span>
                          )}
                        </div>
                      </>
                    ) : verification.reached_target ? (
                      <>
                        <div className="font-bold text-emerald-900">Job card reaches Band {verification.projected_band} (SAP {verification.projected_sap}) ✓</div>
                        <div className="text-emerald-800/80 mt-1">
                          Starting from SAP {verification.current_sap}{verification.current_sap_derived && <span title="Derived from band mid-point" className="text-amber-700">*</span>},
                          this programme adds +{verification.total_uplift} SAP points across {verification.measures_count} measures
                          for {fmtGBP(verification.total_cost)}.
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="font-bold" style={{ color: "var(--hi-orange-deep)" }}>Job card falls short of Band C by {verification.sap_gap} SAP points</div>
                        <div className="text-amber-900/80 mt-1">
                          Starting from SAP {verification.current_sap}, the listed measures reach SAP {verification.projected_sap} (band {verification.projected_band}).
                          Add more measures above to close the gap.
                        </div>
                      </>
                    )}
                    {verification.unknown_uplift_count > 0 && verification.source !== "job_card_sheet" && (
                      <div className="text-[10px] text-amber-700 mt-1">{verification.unknown_uplift_count} measure(s) couldn't be auto-matched — add a "SAP uplift" column in the template to verify them accurately.</div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <Separator className="my-6" />

            {/* Property details grid */}
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Property details</div>
              <div className="grid grid-cols-2 gap-y-3 gap-x-6 text-sm">
                <Detail icon={<Home className="h-3.5 w-3.5" />} label="Type" value={prop.property_type} />
                <Detail icon={<Home className="h-3.5 w-3.5" />} label="Built Form" value={prop.built_form} />
                <Detail icon={<Calendar className="h-3.5 w-3.5" />} label="Age" value={prop.property_age} />
                <Detail icon={<Wrench className="h-3.5 w-3.5" />} label="Walls" value={prop.wall_construction} sub={prop.wall_insulation} />
                <Detail icon={<Wrench className="h-3.5 w-3.5" />} label="Roof" value={prop.roof_construction} sub={prop.joist_insulation} />
                <Detail icon={<Flame className="h-3.5 w-3.5" />} label="Heating" value={prop.space_heating_system} sub={prop.space_heating_fuel} />
              </div>
            </div>

            <Separator className="my-6" />

            {/* History timeline */}
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Change history</div>
              {(!prop.history || prop.history.length === 0) ? (
                <div className="text-sm text-muted-foreground py-4">No recorded changes yet.</div>
              ) : (
                <div className="space-y-3">
                  {prop.history.map((h) => (
                    <div key={h.id} className="flex gap-3 text-sm" data-testid={`history-${h.id}`}>
                      <div className="mt-1.5">
                        {h.type === "band_upgrade" && <ArrowUpRight className="h-4 w-4 text-emerald-700" />}
                        {h.type === "band_downgrade" && <ArrowDownRight className="h-4 w-4 text-amber-700" />}
                        {h.type === "new_certificate" && <ArrowRight className="h-4 w-4 text-slate-700" />}
                        {h.type === "expiring_soon" && <Calendar className="h-4 w-4 text-amber-700" />}
                      </div>
                      <div className="flex-1">
                        <div className="text-xs uppercase tracking-wider font-semibold">{h.type.replace("_", " ")}</div>
                        <div className="text-sm">{h.message}</div>
                        <div className="text-xs text-muted-foreground font-mono mt-0.5">{new Date(h.created_at).toLocaleString("en-GB")}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Delivery edit dialog */}
            <DeliveryEditDialog
              open={!!editingDelivery}
              onClose={() => setEditingDelivery(null)}
              propertyId={propertyId}
              measure={editingDelivery}
              onSaved={(rec) => {
                setDelivery((d) => ({ ...d, [editingDelivery.measure_id]: { ...editingDelivery, ...rec } }));
                setEditingDelivery(null);
                toast.success("Delivery updated");
              }}
            />
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function EPCCard({ title, epc, highlight, onDownload, which, downloading }) {
  const e = epc || {};
  const hasRecord = !!e.band;
  const expired = !!e.expired;
  const expiryStr = e.expiry_date ? new Date(e.expiry_date).toLocaleDateString("en-GB") : null;
  const certNumber = e.certificate_number || e.lmk_key;

  // Previous EPC slot: when nothing is on the gov.uk register, show a clear
  // "no prior EPC" placeholder instead of an empty card. The gov.uk register
  // is the source of truth — only present a previous if it came from gov.
  if (which === "previous" && !hasRecord) {
    return (
      <div className="border border-dashed border-slate-300 p-4 flex flex-col justify-between bg-slate-50/50" data-testid="previous-epc-none">
        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">{title}</div>
        <div className="flex items-center gap-3 mt-3">
          <div className="h-12 w-12 border border-slate-300 bg-white flex items-center justify-center text-slate-300 text-xl font-bold">—</div>
          <div className="text-xs text-slate-500 leading-snug">
            <div className="font-medium text-slate-700">No prior EPC on the gov.uk register</div>
            <div className="mt-0.5">This is the property's first registered certificate.</div>
          </div>
        </div>
        <Button
          size="sm"
          variant="outline"
          disabled
          className="rounded-none border-slate-200 mt-3 h-8 text-xs text-slate-400"
          data-testid="download-previous-certificate"
        >
          <FileText className="h-3 w-3 mr-1.5" />
          No previous certificate
        </Button>
      </div>
    );
  }

  return (
    <div className={`border ${highlight ? "border-slate-900 border-2" : "border-border"} p-4 flex flex-col relative`}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">{title}</div>
        {expired && (
          <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider border border-red-600 text-red-700 bg-red-50" data-testid={`epc-expired-${which}`}>
            Expired
          </span>
        )}
      </div>
      <div className="flex items-center gap-3 mt-3">
        <EPCBadge band={e.band} size="xl" />
        <div>
          <div className="text-xs text-muted-foreground uppercase tracking-wider">SAP Rating</div>
          <div className="font-heading font-black text-2xl">{e.sap_rating ?? "—"}</div>
          {e.co2_emissions != null && (
            <div className="text-xs text-muted-foreground mt-1 font-mono">{Number(e.co2_emissions).toFixed(2)} t CO₂/yr</div>
          )}
        </div>
      </div>
      {e.lodgement_date && (
        <div className="text-[10px] font-mono text-muted-foreground mt-3">
          Lodged {new Date(e.lodgement_date).toLocaleDateString("en-GB")}
          {expiryStr && (
            <span className={expired ? "text-red-700 ml-2" : "ml-2"}>· Expires {expiryStr}</span>
          )}
        </div>
      )}
      <Button
        size="sm"
        variant="outline"
        disabled={!hasRecord || downloading}
        onClick={onDownload}
        className="rounded-none border-slate-300 mt-3 h-8 text-xs"
        data-testid={`download-${which}-certificate`}
        title={certNumber
          ? `Renders the gov.uk certificate ${certNumber} to PDF (takes a few seconds the first time)`
          : "No gov.uk certificate on file — generates a PDF from the locally-held property data"}
      >
        {downloading ? (
          <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
        ) : (
          <FileText className="h-3 w-3 mr-1.5" />
        )}
        {downloading
          ? `Preparing ${title.replace(" EPC", "")} EPC…`
          : `Download ${title.replace(" EPC", "")} EPC (PDF)`}
      </Button>
    </div>
  );
}

function RouteCard({ title, subtitle, accent, bgAccent, route, testId, badge }) {
  if (!route?.ok || !route.measures) return null;
  const risk = route.heritage_risk_summary || {};
  return (
    <div className="border" style={{ borderColor: accent, backgroundColor: bgAccent }} data-testid={testId}>
      <div className="p-3 flex items-center justify-between" style={{ borderBottom: `1px solid ${accent}33` }}>
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color: accent }}>{title}</div>
          <div className="text-[10px] text-muted-foreground">{subtitle}</div>
        </div>
        <div className="text-right">
          <div className="font-heading font-black text-2xl" style={{ color: accent }}>
            £{(route.total_cost || 0).toLocaleString("en-GB")}
          </div>
          <div className="text-[10px] text-muted-foreground">
            {route.measures.length} measure{route.measures.length === 1 ? "" : "s"} · +{route.total_uplift} SAP
          </div>
        </div>
      </div>
      {badge && (
        <div
          className="px-3 py-1.5 text-[10px] font-mono"
          style={{ backgroundColor: `${accent}11`, color: accent, borderBottom: `1px solid ${accent}33` }}
          data-testid={`${testId}-badge`}
        >
          {badge}
        </div>
      )}
      {risk.flagged_count > 0 && (
        <div
          className="px-3 py-2 text-[11px] flex items-start gap-2 border-b"
          style={{ backgroundColor: "#fff7ed", borderColor: "#fed7aa", color: "#9a3412" }}
          data-testid={`${testId}-heritage-banner`}
          title={`Lead time: allow ${risk.max_lead_months} months for planning consent`}
        >
          <ShieldAlert className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>
            <strong>{risk.flagged_count} measure{risk.flagged_count === 1 ? "" : "s"}</strong> may need planning consent
            ({risk.constraints_seen.join(" · ")}){risk.any_listed_consent ? " · Listed Building Consent required" : ""}.
            <span className="ml-1 text-amber-900/80">Allow {risk.max_lead_months} months lead time.</span>
          </span>
        </div>
      )}
      <div className="divide-y" style={{ borderColor: `${accent}22` }}>
        {route.measures.map((m, idx) => (
          <div key={`${title}-${idx}`} className="px-3 py-2 grid grid-cols-12 gap-2 items-center text-xs" data-testid={`${testId}-measure-${idx}`}>
            <div className="col-span-1 font-mono text-slate-400">{idx + 1}.</div>
            <div className="col-span-7">
              <div className="font-medium leading-tight flex items-center gap-1.5 flex-wrap">
                {m.label}
                {m.heritage_risk && (
                  <span
                    className="px-1 py-0.5 text-[9px] font-bold uppercase tracking-wider inline-flex items-center gap-0.5"
                    style={{ backgroundColor: "#fff7ed", color: "#9a3412", border: "1px solid #fed7aa" }}
                    title={`${m.heritage_risk.reason} · ${m.heritage_risk.lead_time_months} mo lead time`}
                    data-testid={`${testId}-risk-${idx}`}
                  >
                    <ShieldAlert className="h-2.5 w-2.5" /> Planning
                  </span>
                )}
              </div>
              <div className="text-[9px] font-mono text-muted-foreground">
                SAP {m.sap_before} → {m.sap_after} ({m.band_after}) · £{m.cost_per_point}/pt
              </div>
            </div>
            <div className="col-span-2 text-right">
              <span className="inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white"
                    style={{ backgroundColor: accent }}>
                +{m.sap_uplift}
              </span>
            </div>
            <div className="col-span-2 text-right font-mono font-bold">£{m.cost.toLocaleString("en-GB")}</div>
          </div>
        ))}
      </div>
      <div className="px-3 py-2 text-[10px]" style={{ color: route.reached_target ? "#15803d" : "#b45309", borderTop: `1px solid ${accent}33` }}>
        {route.reached_target
          ? `Reaches Band ${route.projected_band} (SAP ${route.projected_sap}) ✓`
          : `Stops at SAP ${route.projected_sap} — assessor review needed`}
      </div>
    </div>
  );
}

// Renewables card has two viewing modes (priority = renewables + topped up to
// reach band C, only = renewables in isolation showing the honest ceiling).
// The toggle is part of the card header so the user can see the trade-off
// without leaving the page.
function RenewablesRouteCard({ priority, only, view, onViewChange, extraCost }) {
  const active = view === "only" ? only : priority;
  if (!active?.ok || !active.measures) return null;
  const accent = "#7c3aed";
  const bgAccent = "#f5f3ff";
  return (
    <div className="border" style={{ borderColor: accent, backgroundColor: bgAccent }} data-testid="route-renewables">
      <div className="p-3 flex items-center justify-between" style={{ borderBottom: `1px solid ${accent}33` }}>
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color: accent }}>Renewables</div>
          <div className="text-[10px] text-muted-foreground">
            {view === "only" ? "Renewables only" : "Renewables-first + top-up to C"}
          </div>
        </div>
        <div className="text-right">
          <div className="font-heading font-black text-2xl" style={{ color: accent }}>
            £{(active.total_cost || 0).toLocaleString("en-GB")}
          </div>
          <div className="text-[10px] text-muted-foreground">
            {active.measures.length} measure{active.measures.length === 1 ? "" : "s"} · +{active.total_uplift} SAP
          </div>
        </div>
      </div>
      <div className="px-2 pt-2 pb-1 flex gap-1" style={{ borderBottom: `1px solid ${accent}22` }}>
        <button
          type="button"
          onClick={() => onViewChange("priority")}
          className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 transition-colors ${view === "priority" ? "" : "hover:bg-white"}`}
          style={{
            backgroundColor: view === "priority" ? accent : "transparent",
            color: view === "priority" ? "#ffffff" : accent,
            border: `1px solid ${accent}`,
          }}
          data-testid="renewables-toggle-priority"
        >
          Reach Band C
        </button>
        <button
          type="button"
          onClick={() => onViewChange("only")}
          className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 transition-colors ${view === "only" ? "" : "hover:bg-white"}`}
          style={{
            backgroundColor: view === "only" ? accent : "transparent",
            color: view === "only" ? "#ffffff" : accent,
            border: `1px solid ${accent}`,
          }}
          data-testid="renewables-toggle-only"
        >
          Renewables only
        </button>
      </div>
      {view === "priority" && extraCost != null && extraCost !== 0 && (
        <div
          className="px-3 py-1.5 text-[10px] font-mono"
          style={{ backgroundColor: `${accent}11`, color: accent, borderBottom: `1px solid ${accent}33` }}
          data-testid="renewables-badge"
        >
          {extraCost > 0
            ? `+£${extraCost.toLocaleString("en-GB")} vs cheapest`
            : `−£${Math.abs(extraCost).toLocaleString("en-GB")} vs cheapest`}
        </div>
      )}
      <div className="divide-y" style={{ borderColor: `${accent}22` }}>
        {active.measures.map((m, idx) => (
          <div key={`ren-${idx}`} className="px-3 py-2 grid grid-cols-12 gap-2 items-center text-xs" data-testid={`route-renewables-measure-${idx}`}>
            <div className="col-span-1 font-mono text-slate-400">{idx + 1}.</div>
            <div className="col-span-7">
              <div className="font-medium leading-tight">{m.label}</div>
              <div className="text-[9px] font-mono text-muted-foreground">
                SAP {m.sap_before} → {m.sap_after} ({m.band_after}) · £{m.cost_per_point}/pt
              </div>
            </div>
            <div className="col-span-2 text-right">
              <span className="inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white" style={{ backgroundColor: accent }}>
                +{m.sap_uplift}
              </span>
            </div>
            <div className="col-span-2 text-right font-mono font-bold">£{m.cost.toLocaleString("en-GB")}</div>
          </div>
        ))}
      </div>
      <div className="px-3 py-2 text-[10px]" style={{ color: active.reached_target ? "#15803d" : "#b45309", borderTop: `1px solid ${accent}33` }}>
        {active.reached_target
          ? `Reaches Band ${active.projected_band} (SAP ${active.projected_sap}) ✓`
          : `Reaches Band ${active.projected_band} (SAP ${active.projected_sap}) — short of C`}
      </div>
    </div>
  );
}

// Saved custom plan — appears as a highlighted banner ABOVE the auto routes
// so the operator sees their bespoke plan first.
function SavedCustomPlanCard({ saved, plan, onEdit, onClear }) {
  return (
    <div className="border-2 p-3" style={{ borderColor: "#0891b2", backgroundColor: "#ecfeff" }} data-testid="custom-plan-banner">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold flex items-center gap-1.5" style={{ color: "#0e7490" }}>
            <Wrench className="h-3 w-3" /> Custom plan saved
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            {saved.measure_keys.length} measure{saved.measure_keys.length === 1 ? "" : "s"} ·
            saved by {saved.saved_by || "—"} on {new Date(saved.saved_at).toLocaleDateString("en-GB")}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="font-heading font-black text-xl" style={{ color: "#0e7490" }}>
              £{(plan.total_cost || 0).toLocaleString("en-GB")}
            </div>
            <div className="text-[10px] font-mono">
              {plan.projected_band} · SAP {plan.projected_sap} · +{plan.total_uplift} SAP
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <Button size="sm" variant="outline" className="h-7 text-[10px] uppercase tracking-wider" onClick={onEdit} data-testid="custom-plan-edit">
              Edit
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-[10px] uppercase tracking-wider text-red-700 hover:text-red-800 hover:bg-red-50" onClick={onClear} data-testid="custom-plan-clear">
              Clear
            </Button>
          </div>
        </div>
      </div>
      {plan.measures?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {plan.measures.map((m, idx) => (
            <span key={idx} className="text-[10px] font-mono px-2 py-0.5" style={{ backgroundColor: "#ffffff", border: "1px solid #67e8f9", color: "#155e75" }}>
              {m.label} <span style={{ color: "#0891b2" }}>+{m.sap_uplift}</span>
            </span>
          ))}
        </div>
      )}
      {plan.reached_target ? (
        <div className="mt-2 text-[10px] text-emerald-700 font-mono">Reaches Band {plan.projected_band} (SAP {plan.projected_sap}) ✓</div>
      ) : (
        <div className="mt-2 text-[10px] text-amber-700 font-mono">Stops at Band {plan.projected_band} (SAP {plan.projected_sap}) — short of C</div>
      )}
    </div>
  );
}

// Custom-plan editor — modal lists every RdSAP-10 library measure with
// checkboxes, grouped by category. As the user toggles measures the totals
// at the bottom recompute server-side so SAP/£ stay accurate.
function CustomPlanEditor({ propertyId, open, onClose, currentSap, currentBand, initialKeys, onSaved }) {
  const [library, setLibrary] = useState(null);
  const [selected, setSelected] = useState(() => new Set(initialKeys || []));
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/measures/library").then(r => setLibrary(r.data)).catch(() => setLibrary({ items: [] }));
  }, []);

  useEffect(() => {
    setSelected(new Set(initialKeys || []));
  }, [initialKeys]);

  // Compute preview locally for speed — same maths as the backend. Cheap
  // enough that we don't need a server round-trip on every checkbox click.
  useEffect(() => {
    if (!library) { setPreview(null); return; }
    const byKey = Object.fromEntries(library.items.map(m => [m.key, m]));
    let sap = currentSap || 0;
    let totalCost = 0;
    let totalUplift = 0;
    const measures = [];
    const usedGroups = {};
    for (const key of selected) {
      const m = byKey[key];
      if (!m) continue;
      usedGroups[m.group] = (usedGroups[m.group] || 0) + 1;
      measures.push({ ...m, sap_before: sap, sap_after: sap + m.sap_uplift });
      sap += m.sap_uplift;
      totalCost += m.cost;
      totalUplift += m.sap_uplift;
    }
    const band = sap >= 92 ? "A" : sap >= 81 ? "B" : sap >= 69 ? "C"
                : sap >= 55 ? "D" : sap >= 39 ? "E" : sap >= 21 ? "F" : "G";
    const dupGroups = Object.entries(usedGroups).filter(([, n]) => n > 1).map(([g]) => g);
    setPreview({
      measures, total_cost: totalCost, total_uplift: totalUplift,
      projected_sap: sap, projected_band: band,
      reached_target: sap >= 69,
      duplicate_groups: dupGroups,
    });
  }, [selected, library, currentSap]);

  const handleToggle = (key) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/properties/${propertyId}/custom-plan`, {
        measure_keys: Array.from(selected),
      });
      onSaved?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save custom plan.");
    } finally {
      setSaving(false);
    }
  };

  // Group library items by category for a scannable picker
  const grouped = (library?.items || []).reduce((acc, m) => {
    (acc[m.category] = acc[m.category] || []).push(m);
    return acc;
  }, {});
  const orderedCategories = [
    "Renewables", "Heating System", "Heating Controls",
    "Insulation — Walls", "Insulation — Roof", "Insulation — Floor",
    "Glazing & Doors", "Hot Water", "Heat Recovery", "Ventilation", "Lighting", "Other",
  ].filter(c => grouped[c]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-4xl max-h-[88vh] overflow-hidden flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-3 border-b">
          <DialogTitle className="font-heading text-xl">Customise Path-to-C plan</DialogTitle>
          <DialogDescription className="text-xs">
            Tick the retrofit measures you want included for this property. Live SAP / cost totals update as you go.
            Starting point: Band <strong>{currentBand}</strong> · SAP {currentSap}.
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {!library ? (
            <div className="text-sm text-muted-foreground">Loading library…</div>
          ) : (
            <div className="space-y-5">
              {orderedCategories.map(cat => (
                <div key={cat}>
                  <div className="text-[11px] uppercase tracking-[0.18em] font-bold mb-2" style={{ color: "var(--hi-olive)" }}>
                    {cat}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {grouped[cat].map(m => {
                      const isOn = selected.has(m.key);
                      return (
                        <label
                          key={m.key}
                          className={`flex items-start gap-3 px-3 py-2 cursor-pointer border transition-colors ${isOn ? "" : "hover:bg-slate-50"}`}
                          style={{
                            borderColor: isOn ? "var(--hi-olive)" : "#e5e7eb",
                            backgroundColor: isOn ? "#f6fbe9" : "#ffffff",
                          }}
                          data-testid={`measure-picker-${m.key}`}
                        >
                          <input
                            type="checkbox"
                            className="mt-1 h-4 w-4 accent-emerald-700"
                            checked={isOn}
                            onChange={() => handleToggle(m.key)}
                            data-testid={`measure-checkbox-${m.key}`}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium leading-tight flex items-center gap-2 flex-wrap">
                              {m.label}
                              {m.is_renewable && (
                                <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider px-1 py-0.5" style={{ backgroundColor: "#f5f3ff", color: "#7c3aed", border: "1px solid #ddd6fe" }}>
                                  Renewable
                                </span>
                              )}
                            </div>
                            <div className="text-[11px] font-mono text-muted-foreground mt-0.5">
                              +{m.sap_uplift} SAP · £{m.cost.toLocaleString("en-GB")} · £{Math.round(m.cost / Math.max(1, m.sap_uplift))}/pt
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Sticky preview footer */}
        <div className="border-t bg-slate-50 px-6 py-4 flex items-center justify-between gap-4 flex-wrap" data-testid="custom-plan-preview">
          <div className="flex items-center gap-4 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Projected</div>
              <div className="flex items-center gap-1.5">
                <EPCBadge band={preview?.projected_band || currentBand} size="sm" />
                <span className="font-mono text-sm">SAP {preview?.projected_sap ?? currentSap}</span>
                {preview?.reached_target && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-700" />}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Total cost</div>
              <div className="font-heading text-2xl font-black" style={{ color: "var(--hi-olive)" }}>
                £{(preview?.total_cost || 0).toLocaleString("en-GB")}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">+ SAP</div>
              <div className="font-mono text-sm">+{preview?.total_uplift || 0}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Measures</div>
              <div className="font-mono text-sm">{selected.size}</div>
            </div>
          </div>
          {preview?.duplicate_groups?.length > 0 && (
            <div className="text-[11px] text-amber-700 flex items-center gap-1.5" data-testid="custom-plan-dup-warn">
              <ShieldAlert className="h-3.5 w-3.5" />
              Two measures in the same group ({preview.duplicate_groups.join(", ")}) — they may not stack on site.
            </div>
          )}
          <DialogFooter className="m-0 gap-2">
            <Button variant="outline" size="sm" onClick={onClose} data-testid="custom-plan-cancel">Cancel</Button>
            <Button size="sm" onClick={handleSave} disabled={saving || selected.size === 0} data-testid="custom-plan-save">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" /> : null}
              Save plan
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Detail({ icon, label, value, sub }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-muted-foreground">{icon}{label}</div>
      <div className="font-medium mt-0.5">{value || "—"}</div>
      {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

function DeliveryEditDialog({ open, onClose, propertyId, measure, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!measure) return;
    setForm({
      state: measure.state || "planned",
      contractor: measure.contractor || "",
      instructed_date: measure.instructed_date || "",
      started_date: measure.started_date || "",
      completed_date: measure.completed_date || "",
      actual_cost: measure.actual_cost ?? "",
      notes: measure.notes || "",
    });
  }, [measure]);

  if (!measure) return null;

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        state: form.state,
        contractor: form.contractor || null,
        instructed_date: form.instructed_date || null,
        started_date: form.started_date || null,
        completed_date: form.completed_date || null,
        actual_cost: form.actual_cost === "" ? null : Number(form.actual_cost),
        notes: form.notes || null,
      };
      const { data } = await api.put(`/properties/${propertyId}/measures/${measure.measure_id}/delivery`, payload);
      onSaved(data.record);
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-xl" data-testid="delivery-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading font-black">Project delivery</DialogTitle>
          <DialogDescription>
            <span className="font-medium">{measure.label}</span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-wider font-bold">State</Label>
              <Select value={form.state} onValueChange={(v) => setForm((f) => ({ ...f, state: v }))}>
                <SelectTrigger className="rounded-none border-slate-300 h-10" data-testid="delivery-state-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(STATE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-wider font-bold">Contractor</Label>
              <Input value={form.contractor} onChange={(e) => setForm((f) => ({ ...f, contractor: e.target.value }))} placeholder="e.g. Acme Retrofit Ltd" className="rounded-none border-slate-300 h-10" data-testid="delivery-contractor" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-wider font-bold">Instructed</Label>
              <Input type="date" value={form.instructed_date} onChange={(e) => setForm((f) => ({ ...f, instructed_date: e.target.value }))} className="rounded-none border-slate-300 h-10" data-testid="delivery-instructed-date" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-wider font-bold">Started</Label>
              <Input type="date" value={form.started_date} onChange={(e) => setForm((f) => ({ ...f, started_date: e.target.value }))} className="rounded-none border-slate-300 h-10" data-testid="delivery-started-date" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-wider font-bold">Completed</Label>
              <Input type="date" value={form.completed_date} onChange={(e) => setForm((f) => ({ ...f, completed_date: e.target.value }))} className="rounded-none border-slate-300 h-10" data-testid="delivery-completed-date" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-wider font-bold">Actual cost (£)</Label>
            <Input type="number" min={0} step={10} value={form.actual_cost} onChange={(e) => setForm((f) => ({ ...f, actual_cost: e.target.value }))} placeholder="final paid amount" className="rounded-none border-slate-300 h-10 font-mono" data-testid="delivery-actual-cost" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-wider font-bold">Notes</Label>
            <Textarea rows={3} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="e.g. Awaiting scaffold · resident sign-off pending" className="rounded-none border-slate-300" data-testid="delivery-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none border-slate-300" data-testid="delivery-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="rounded-none text-white" style={{ backgroundColor: "var(--hi-olive)" }} data-testid="delivery-save">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Save delivery
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* -------------------------------------------------------------------------
 * Survey-status panel — record contact attempts when a property is unknown-
 * band or otherwise needs a fresh assessor visit. Editor+ role required by
 * the backend; the panel is read-only for Viewer users.
 * ------------------------------------------------------------------------- */
const SURVEY_STATE_META = {
  not_attempted:        { label: "Not attempted yet",     accent: "#64748b", bg: "#f1f5f9", border: "#cbd5e1" },
  attempted_no_contact: { label: "Attempted — No contact", accent: "#b45309", bg: "#fffbeb", border: "#f59e0b" },
  refused:              { label: "Resident refused access",accent: "#7f1d1d", bg: "#fef2f2", border: "#dc2626" },
  vacant:               { label: "Property vacant",        accent: "#0f172a", bg: "#f8fafc", border: "#475569" },
  surveyed:             { label: "Survey complete",        accent: "#065f46", bg: "#ecfdf5", border: "#10b981" },
};

function SurveyStatusPanel({ prop, onSaved }) {
  const surv = prop.survey_status || {};
  const state = surv.state || "not_attempted";
  const meta = SURVEY_STATE_META[state] || SURVEY_STATE_META.not_attempted;
  const lastAttempt = surv.last_attempt;
  const history = surv.history || [];
  const isUnknown = !["A", "B", "C", "D", "E", "F", "G"].includes(prop.current_epc?.band);

  const [open, setOpen] = useState(false);
  const [newState, setNewState] = useState(state);
  const [method, setMethod] = useState("door");
  const [notes, setNotes] = useState("");
  const [increment, setIncrement] = useState(true);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/properties/${prop.id}/survey`, {
        state: newState, method, notes, increment_attempt: increment,
      });
      toast.success("Survey attempt recorded");
      setOpen(false);
      setNotes("");
      if (onSaved) await onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not record attempt");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-6 border" style={{ borderColor: meta.border, backgroundColor: meta.bg }} data-testid="survey-status-panel">
      <div className="px-4 py-3 flex items-center justify-between gap-3" style={{ borderBottom: `1px solid ${meta.border}66` }}>
        <div>
          <div className="text-xs uppercase tracking-[0.18em] font-bold" style={{ color: meta.accent }}>
            Survey status {isUnknown && state === "not_attempted" && (
              <span className="ml-2 px-1.5 py-0.5 text-[9px] bg-amber-200 text-amber-900 normal-case tracking-normal">
                Unknown band — needs assessor
              </span>
            )}
          </div>
          <div className="font-heading font-bold text-lg mt-0.5" style={{ color: meta.accent }}>{meta.label}</div>
          <div className="text-xs text-slate-600 mt-1">
            {(surv.attempts || 0) > 0 ? (
              <span data-testid="survey-attempt-count">
                <strong>{surv.attempts}</strong> attempt{surv.attempts === 1 ? "" : "s"} logged
                {lastAttempt?.at && ` · last ${new Date(lastAttempt.at).toLocaleDateString("en-GB")} by ${lastAttempt.by || "—"}${lastAttempt.method ? ` (${lastAttempt.method})` : ""}`}
              </span>
            ) : (
              <span>No contact attempts logged yet.</span>
            )}
          </div>
          {lastAttempt?.notes && (
            <div className="text-xs text-slate-700 italic mt-1" data-testid="survey-last-notes">"{lastAttempt.notes}"</div>
          )}
        </div>
        <Button
          size="sm"
          onClick={() => setOpen((o) => !o)}
          className="rounded-none whitespace-nowrap"
          style={{ backgroundColor: meta.accent, color: "white" }}
          data-testid="log-survey-attempt-btn"
        >
          {open ? "Cancel" : "Log attempt"}
        </Button>
      </div>

      {open && (
        <div className="px-4 py-3 space-y-3 bg-white/80" data-testid="survey-status-form">
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-xs">
              <span className="text-slate-500 uppercase tracking-wider text-[10px] font-bold">Outcome</span>
              <select
                value={newState}
                onChange={(e) => setNewState(e.target.value)}
                className="w-full mt-1 border border-slate-300 px-2 py-1.5 text-sm rounded-none"
                data-testid="survey-state-select"
              >
                {Object.entries(SURVEY_STATE_META).map(([k, m]) => (
                  <option key={k} value={k}>{m.label}</option>
                ))}
              </select>
            </label>
            <label className="block text-xs">
              <span className="text-slate-500 uppercase tracking-wider text-[10px] font-bold">Method</span>
              <select
                value={method}
                onChange={(e) => setMethod(e.target.value)}
                className="w-full mt-1 border border-slate-300 px-2 py-1.5 text-sm rounded-none"
                data-testid="survey-method-select"
              >
                <option value="door">Door knock</option>
                <option value="phone">Phone call</option>
                <option value="letter">Letter posted</option>
                <option value="email">Email sent</option>
                <option value="other">Other</option>
              </select>
            </label>
          </div>
          <label className="block text-xs">
            <span className="text-slate-500 uppercase tracking-wider text-[10px] font-bold">Notes (optional)</span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="e.g. no answer at 14:30, posted card asking to call back"
              className="w-full mt-1 border border-slate-300 px-2 py-1.5 text-sm rounded-none"
              data-testid="survey-notes-textarea"
            />
          </label>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={increment}
              onChange={(e) => setIncrement(e.target.checked)}
              data-testid="survey-increment-checkbox"
            />
            Count as a new contact attempt (uncheck for back-office state changes)
          </label>
          <div className="flex justify-end">
            <Button size="sm" onClick={save} disabled={saving} className="rounded-none" style={{ backgroundColor: meta.accent, color: "white" }} data-testid="survey-save-btn">
              {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
              Record attempt
            </Button>
          </div>
        </div>
      )}

      {history.length > 1 && (
        <details className="px-4 py-2 text-xs bg-white/40" data-testid="survey-history">
          <summary className="cursor-pointer text-slate-600 hover:text-slate-900">
            Earlier attempts ({history.length - 1})
          </summary>
          <ul className="mt-2 space-y-1 text-slate-600">
            {history.slice(1).map((h, i) => (
              <li key={i} className="font-mono text-[11px]">
                {h.at ? new Date(h.at).toLocaleDateString("en-GB") : "—"} · {h.by || "—"} · {SURVEY_STATE_META[h.state]?.label || h.state}{h.method ? ` (${h.method})` : ""}
                {h.notes && <span className="italic"> — "{h.notes}"</span>}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}


/* -------------------------------------------------------------------------
 * Heritage / planning-constraint panel — explains WHAT the property sits in
 * (Conservation Area, Listed Building, Article 4 etc.) AND what that means
 * for each retrofit measure. Replaces the tiny badges with a proper section
 * so the operator can see consent implications at a glance.
 * ------------------------------------------------------------------------- */
const HERITAGE_DATASET_META = {
  "conservation-area":          { label: "Conservation Area",      severity: "consent",   what: "External alterations (cladding, render, windows, roof colour, ASHP units, solar) need planning consent. Article 4 Direction may extend this to materials." },
  "listed-building":            { label: "Listed Building",        severity: "block",     what: "Listed Building Consent required for ANY change that affects character — internal AND external. Treat EWI, replacement windows, ASHP and solar as planning-blocked by default." },
  "article-4-direction-area":   { label: "Article 4 Direction",    severity: "consent",   what: "Removes Permitted Development rights for the listed elements (often windows, render, roofs). Solar/ASHP may need full planning consent." },
  "area-of-outstanding-natural-beauty": { label: "AONB",            severity: "consent",   what: "External-facing measures (ASHP, solar, EWI) face heightened landscape-impact tests. Local authority may require visual screening." },
  "national-park":              { label: "National Park",          severity: "consent",   what: "Strictest landscape protection — ASHP siting, solar visibility, roof colour and material all subject to consent." },
  "world-heritage-site":        { label: "World Heritage Site",    severity: "block",     what: "All external changes go through UNESCO impact assessment + Historic England. Assume EWI / solar / ASHP need pre-application advice." },
  "scheduled-monument":         { label: "Scheduled Monument",     severity: "block",     what: "Scheduled Monument Consent required for ground disturbance (heat-pump trenches, solar mounts) and any fabric changes." },
  "tree-preservation-order":    { label: "Tree Preservation Order",severity: "consent",   what: "TPO trees can constrain ASHP siting, solar panel solar-gain and EWI scaffolding. Pruning needs LPA consent." },
  "green-belt":                 { label: "Green Belt",             severity: "consent",   what: "Extensions / outbuildings to house ASHP plant face stricter openness tests. Usually OK for wall-mounted ASHP." },
  "flood-risk-zone":            { label: "Flood Risk Zone",        severity: "block",     what: "ASHP outdoor units, ground-source loops and floor insulation all need flood-resilient siting. Avoid below DPC. EA may require a flood risk assessment." },
};

function HeritagePanel({ heritage, propertyId, onScanned }) {
  const [scanning, setScanning] = useState(false);
  const constraints = heritage?.constraints || [];
  const hasConstraints = heritage?.has_constraints && constraints.length > 0;
  const pending = !heritage || !heritage.ok;

  const scanNow = async () => {
    setScanning(true);
    try {
      const { data } = await api.get(`/properties/${propertyId}/heritage`, { params: { refresh: true } });
      if (!data?.ok) {
        toast.error("Planning API still unavailable — try again in a few minutes.");
      } else if (data?.has_constraints) {
        toast.success(`Found ${data.constraints?.length || 0} planning constraint${(data.constraints?.length || 0) === 1 ? "" : "s"}`);
        onScanned?.();
      } else {
        toast.success("Heritage clear — no planning constraints at this address.");
        onScanned?.();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Heritage scan failed");
    } finally {
      setScanning(false);
    }
  };

  if (pending) {
    return (
      <div className="mt-4 border border-slate-300 bg-slate-50 px-4 py-3 flex items-center justify-between gap-3" data-testid="heritage-panel-pending">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-600 mb-1 flex items-center gap-2">
            <ShieldAlert className="h-3.5 w-3.5" /> Heritage / Planning — Lookup pending
          </div>
          <div className="text-xs text-slate-700">
            We haven't scanned this property against UK gov planning data yet (or the upstream API was down at the time). Click <strong>Scan now</strong> to retry, or wait — the background monitor will pick it up automatically once the API recovers.
          </div>
        </div>
        <Button
          size="sm"
          onClick={scanNow}
          disabled={scanning}
          className="rounded-none whitespace-nowrap bg-slate-800 hover:bg-slate-900 text-white"
          data-testid="heritage-scan-now-btn"
        >
          {scanning ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
          {scanning ? "Scanning…" : "Scan now"}
        </Button>
      </div>
    );
  }
  if (!hasConstraints) {
    return (
      <div className="mt-4 border border-emerald-200 bg-emerald-50/60 px-4 py-3" data-testid="heritage-panel-clear">
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700 mb-1 flex items-center gap-2">
          <ShieldCheck className="h-3.5 w-3.5" /> Heritage / planning — Clear
        </div>
        <div className="text-xs text-emerald-900">
          Scanned the UK gov planning register at this address — no conservation,
          listing, AONB, Article-4, flood-zone, green-belt or scheduled-monument
          constraints. Retrofit measures can proceed under standard Permitted
          Development rules.
        </div>
        <button onClick={scanNow} disabled={scanning} className="mt-2 text-[10px] uppercase tracking-wider underline text-emerald-700 hover:text-emerald-900" data-testid="heritage-rescan-btn">
          {scanning ? "Re-scanning…" : "Re-scan"}
        </button>
      </div>
    );
  }
  return (
    <div className="mt-4 border border-amber-400 bg-amber-50/40" data-testid="heritage-panel-active">
      <div className="px-4 py-2 border-b border-amber-300 bg-amber-100/40 flex items-center gap-2">
        <ShieldAlert className="h-4 w-4 text-amber-800" />
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber-800">Heritage / Planning — {constraints.length} constraint{constraints.length === 1 ? "" : "s"}</div>
          <div className="text-xs text-amber-900">
            This property sits inside designated areas — retrofit measures below may need planning consent.
          </div>
        </div>
        <button onClick={scanNow} disabled={scanning} className="text-[10px] uppercase tracking-wider underline text-amber-800 hover:text-amber-950 whitespace-nowrap" data-testid="heritage-rescan-btn">
          {scanning ? "Re-scanning…" : "Re-scan"}
        </button>
      </div>
      <ul className="divide-y divide-amber-200">
        {constraints.map((c, i) => {
          const meta = HERITAGE_DATASET_META[c.dataset] || { label: c.label || c.dataset, severity: c.severity || "consent", what: c.description || "Local authority planning consent likely required." };
          const isBlock = meta.severity === "block" || c.severity === "block";
          return (
            <li key={i} className="px-4 py-2.5" data-testid={`heritage-row-${c.dataset}`}>
              <div className="flex items-start gap-3">
                <span
                  className="mt-0.5 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider border whitespace-nowrap"
                  style={isBlock
                    ? { backgroundColor: "#fef2f2", color: "#7f1d1d", borderColor: "#dc2626" }
                    : { backgroundColor: "#fff8ed", color: "var(--hi-orange-deep)", borderColor: "var(--hi-orange)" }}
                >
                  {isBlock ? "Consent required" : "Consent likely"}
                </span>
                <div className="flex-1">
                  <div className="text-sm font-bold text-slate-900">{meta.label}</div>
                  {c.name && <div className="text-[11px] font-mono text-slate-500">{c.name}</div>}
                  <div className="text-xs text-slate-700 mt-1">{meta.what}</div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
